/**
 * The weekly check-in's server functions, for the signed-in coach only. Thin
 * glue over `~/lib/server/nova/routines/weekly-check-in`.
 *
 * Aliases: weekly check-in api, check-in page data, suggest weekly move,
 * complete check-in
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireNovaCoachAuth } from "~/lib/coach-middleware";
import {
  lastMoveAnswerSchema,
  weeklyCheckInAnswersSchema,
  weeklyTypedNumbersSchema,
} from "~/lib/shared/nova/weekly-check-in";

// Type-only re-exports, erased at build, so a component never imports from
// `~/lib/server`.
export type {
  CurrentCheckIn,
  PastCheckIn,
  SuggestedMove,
  WeeklyCheckInView,
  WeeklyMoveSuggestion,
} from "~/lib/server/nova/routines/weekly-check-in";

export const getMyWeeklyCheckIn = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { readWeeklyCheckIn } =
      await import("~/lib/server/nova/routines/weekly-check-in");
    return readWeeklyCheckIn(context.coach);
  });

export const suggestMyWeeklyMove = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(
    z.object({
      runId: z.string().uuid(),
      typed: weeklyTypedNumbersSchema,
      lastMove: lastMoveAnswerSchema.nullable(),
    }),
  )
  .handler(async ({ data, context }) => {
    const { suggestWeeklyMove } =
      await import("~/lib/server/nova/routines/weekly-check-in");
    return suggestWeeklyMove(context.coach, data);
  });

export const completeMyWeeklyCheckIn = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(weeklyCheckInAnswersSchema)
  .handler(async ({ data, context }) => {
    const { completeWeeklyCheckIn } =
      await import("~/lib/server/nova/routines/weekly-check-in");
    return completeWeeklyCheckIn(context.coach, data);
  });
